import { useState } from "react";
import { AnimatePresence } from "framer-motion";
import { assetUrl } from "@/lib/constants";
import { Bindi, Reveal, SectionHeading } from "@/components/motifs";
import { ImageLightbox } from "@/components/Lightbox";

const EventCard = ({ ev, index, onOpen }) => {
  const allImages = [ev.image, ...(ev.images || [])].filter(Boolean);
  return (
    <Reveal delay={0.05 * index}>
      <article
        className="group grid gap-6 border border-border bg-background p-6 transition-colors duration-500 hover:bg-paper sm:grid-cols-[230px_1fr] sm:p-8"
        data-testid={`event-card-${index}`}
      >
        <div>
          {ev.image && (
            <button
              onClick={() => onOpen(allImages, 0, ev.title)}
              className="relative block w-full overflow-hidden border border-foreground/15"
              aria-label={`View invitation for ${ev.title}`}
              data-testid={`event-invite-${index}`}
            >
              <img
                src={assetUrl(ev.image)}
                alt={`${ev.title} — invitation`}
                loading="lazy"
                className="h-44 w-full object-cover transition-transform duration-700 group-hover:scale-[1.03] sm:h-48"
              />
              <span className="absolute bottom-2 left-2 bg-background/90 px-2 py-1 font-mono text-[9px] uppercase tracking-[0.18em] text-foreground">
                Invitation
              </span>
            </button>
          )}
          {(ev.images || []).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {ev.images.map((p, i) => (
                <button
                  key={p}
                  onClick={() => onOpen(allImages, i + 1, ev.title)}
                  className="overflow-hidden border border-foreground/15 transition-colors hover:border-accent"
                  aria-label={`View picture ${i + 2} of ${ev.title}`}
                  data-testid={`event-thumb-${index}-${i}`}
                >
                  <img src={assetUrl(p)} alt={`${ev.title} — picture ${i + 2}`} loading="lazy" className="h-14 w-16 object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="chip">{ev.event_type}</span>
            {ev.status === "upcoming" ? (
              <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-accent">
                <Bindi size={6} pulse /> Upcoming
              </span>
            ) : (
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Past</span>
            )}
          </div>
          <h3 className="mt-4 font-serif text-xl font-medium leading-snug tracking-tight sm:text-2xl">{ev.title}</h3>
          {[ev.date_text, ev.venue, ev.city].filter(Boolean).length > 0 && (
            <p className="eyebrow mt-2 text-muted-foreground">
              {[ev.date_text, ev.venue, ev.city].filter(Boolean).join(" · ")}
            </p>
          )}
          {ev.description && (
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">{ev.description}</p>
          )}
        </div>
      </article>
    </Reveal>
  );
};

const Placeholder = () => (
  <>
    <Reveal>
      <p className="max-w-2xl font-serif text-xl italic leading-relaxed text-muted-foreground sm:text-2xl">
        My archive is still being written — exhibitions, honours and milestones will appear here
        as my journey continues.
      </p>
    </Reveal>
    <div className="mt-14 grid gap-px overflow-hidden border border-border bg-border sm:grid-cols-3">
      {["First documented exhibition", "Group & workshop showcases", "The archive grows"].map((slot, i) => (
        <Reveal key={slot} delay={0.07 * i} className="bg-background">
          <div className="flex h-full min-h-[160px] flex-col justify-between p-7">
            <span className="font-mono text-xs tracking-[0.3em] text-foreground/25">{String(i + 1).padStart(2, "0")}</span>
            <div className="flex items-center gap-3">
              <Bindi size={7} className="opacity-40" />
              <p className="font-serif text-lg italic text-muted-foreground">{slot}</p>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </>
);

export const ExhibitionsChapter = ({ events = [], headingAs }) => {
  const [lightbox, setLightbox] = useState(null);
  const upcoming = events.filter((e) => e.status === "upcoming");
  const past = events.filter((e) => e.status !== "upcoming");
  const open = (images, index, title) => setLightbox({ images, index, title });

  return (
    <section id="exhibitions" className="paper-surface border-y border-border" data-testid="exhibitions-section">
      <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <SectionHeading
          number="06"
          title="Exhibitions & Journey"
          note={events.length ? "An active practice, documented" : "To be documented"}
          as={headingAs}
        />
        {events.length === 0 ? (
          <Placeholder />
        ) : (
          <div className="space-y-12">
            {upcoming.length > 0 && (
              <div>
                <Reveal>
                  <p className="eyebrow mb-6 flex items-center gap-3 text-accent">
                    <Bindi size={7} pulse /> Forthcoming
                  </p>
                </Reveal>
                <div className="space-y-5">
                  {upcoming.map((ev, i) => (
                    <EventCard key={ev.id} ev={ev} index={i} onOpen={open} />
                  ))}
                </div>
              </div>
            )}
            {past.length > 0 && (
              <div>
                <Reveal>
                  <p className="eyebrow mb-6 text-muted-foreground">Past engagements</p>
                </Reveal>
                <div className="space-y-5">
                  {past.map((ev, i) => (
                    <EventCard key={ev.id} ev={ev} index={i + upcoming.length} onOpen={open} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <AnimatePresence>
        {lightbox && (
          <ImageLightbox
            images={lightbox.images}
            title={lightbox.title}
            index={lightbox.index}
            onClose={() => setLightbox(null)}
            onIndexChange={(i) => setLightbox((s) => ({ ...s, index: i }))}
          />
        )}
      </AnimatePresence>
    </section>
  );
};
